import axios, { AxiosError } from 'axios';
import { JoinRequestFormData, JoinRequestResponse, ApiError, Question } from '../types';

// Get configuration from environment variables
const getConfig = () => ({
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'https://api.istqim.com/api'
});

// Create axios instance with default configuration
const api = axios.create({
  baseURL: getConfig().apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds
});

// Function to extract mosque ID from header (will be updated when decryption method is provided)
const getMosqueIdFromHeader = (): string | null => {
  // Get the encrypted mosque ID from the URL
  const pathParts = window.location.pathname.split('/');
  const encryptedMosqueId = pathParts[pathParts.length - 1];
  
  if (!encryptedMosqueId) {
    console.warn('Encrypted mosque ID not found in URL');
    return null;
  }
  
  return encryptedMosqueId;
};

// Add request interceptor to include mosque ID
api.interceptors.request.use((config) => {
  const encryptedMosqueId = getMosqueIdFromHeader();
  if (encryptedMosqueId) {
    config.headers['x-mosque-id'] = encryptedMosqueId;
  }
  return config;
});

// Submit join request
export const submitJoinRequest = async (
  formData: JoinRequestFormData
): Promise<JoinRequestResponse> => {
  try {
    const response = await api.post<JoinRequestResponse>('/join-requests', formData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      
      if (axiosError.response?.data) {
        // Server returned an error response
        throw new Error(axiosError.response.data.message || 'فشل في إرسال طلب الانضمام');
      } else if (axiosError.request) {
        // Network error
        throw new Error('لا يمكن الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت.');
      } else {
        // Other error
        throw new Error('حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.');
      }
    }
    
    // Non-axios error
    throw new Error('حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.');
  }
};

// Health check endpoint (optional)
export const healthCheck = async (): Promise<boolean> => {
  try {
    await api.get('/health');
    return true;
  } catch {
    return false;
  }
};

// Fetch dynamic questions
export const fetchQuestions = async (): Promise<Question[]> => {
  try {
    const response = await api.get<Question[]>('join-requests/questions');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      
      if (axiosError.response?.data) {
        // Server returned an error response
        throw new Error(axiosError.response.data.message || 'فشل في جلب الأسئلة');
      } else if (axiosError.request) {
        // Network error
        throw new Error('لا يمكن الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت.');
      } else {
        // Other error
        throw new Error('حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.');
      }
    }
    
    // Non-axios error
    throw new Error('حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.');
  }
}; 